import React, { useState } from 'react';
import {
  Send,
  Mail,
  User,
  MessageSquare,
  MessageSquareMore,
  Loader2,
  Linkedin,
  Github,
  ArrowUp,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { usePortfolio } from '@/context/PortfolioContext';
import { contactAPI } from '@/services/api';
import { SectionShell, SectionInner, SectionHeader } from '@/components/section/sectionLayout';
import { toast } from 'sonner';
import Chatbot from '@/components/ChatBot';


const Contact = () => {
  const { portfolioData } = usePortfolio();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await contactAPI.sendMessage(formData);
      toast.success('Message envoyé !', 'Je vous répondrai dans les plus brefs délais.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Erreur', error.message || "Une erreur est survenue lors de l'envoi du message.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hero = portfolioData?.hero;

  return (
    <SectionShell id="contact" variant="alt" className="pb-28 sm:pb-24">
      <SectionInner>
        <SectionHeader
          title="Contactez-"
          highlight="moi"
          highlightGlue=""
          description="Une question ? Un projet ? N'hésitez pas à me contacter"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div className="order-2 space-y-4 lg:order-1 lg:space-y-6">
            <Card className="border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-gray-900 dark:text-white sm:text-lg">Email</CardTitle>
              </CardHeader>
              <CardContent>
                <a
                  href={`mailto:${hero?.email || ''}`}
                  className="flex items-start gap-2 break-all text-sm text-gray-600 hover:text-emerald-500 dark:text-gray-400 sm:text-base"
                >
                  <Mail className="mt-0.5 h-4 w-4 shrink-0" />
                  {hero?.email || 'Email non disponible'}
                </a>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-gray-900 dark:text-white sm:text-lg">
                  Coordonnées
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-gray-600 dark:text-gray-400 sm:text-base">
                <p>Téléphone : {hero?.phone || '—'}</p>
                <p>Localisation : {hero?.location || '—'}</p>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-gray-900 dark:text-white sm:text-lg">
                  Réseaux sociaux
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3">
                <a
                  href={hero?.github || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-emerald-500 dark:text-gray-400"
                >
                  <Github className="h-4 w-4" /> GitHub
                </a>
                <a
                  href={hero?.linkedin || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-emerald-500 dark:text-gray-400"
                >
                  <Linkedin className="h-4 w-4" /> LinkedIn
                </a>
                <a
                  href={hero?.whatsapp || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-emerald-500 dark:text-gray-400"
                >
                  <MessageSquareMore className="h-4 w-4" /> WhatsApp
                </a>
              </CardContent>
            </Card>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-2">
            <Card className="border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e]">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900 dark:text-white sm:text-xl">
                  Envoyer un message
                </CardTitle>
                <CardDescription className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
                  Remplissez le formulaire et je vous répondrai rapidement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom complet</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        minLength={3}
                        maxLength={50}
                        onChange={handleInputChange}
                        placeholder="Votre nom"
                        required
                        className="pl-11 dark:bg-[#0f1419]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="votre@email.com"
                        required
                        className="pl-11 dark:bg-[#0f1419]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        minLength={10}
                        maxLength={5000}
                        onChange={handleInputChange}
                        placeholder="Votre message..."
                        required
                        rows={5}
                        className="resize-none pl-11 dark:bg-[#0f1419]"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-emerald-500 py-5 text-base text-white hover:bg-emerald-600 sm:py-6 sm:text-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Envoyer le message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </SectionInner>

      <Chatbot />

      <div className="fixed bottom-20 right-4 z-40 sm:bottom-6 sm:right-6">
        <Button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          size="icon"
          variant="outline"
          className="h-11 w-11 rounded-full border-2 border-emerald-400 bg-white/90 text-emerald-600 shadow-lg backdrop-blur dark:bg-[#0a0e1a]/90 dark:text-emerald-400"
          aria-label="Retour en haut"
        >
          <ArrowUp className="h-5 w-5" />
        </Button>
      </div>
    </SectionShell>
  );
};

export default Contact;
